import React, { createContext, useContext, useState } from "react";

// Definir el tipo de los mensajes
import axios from "axios";

interface Message {
  contextName?: string;
  temperatura?: number;
  conductividad?: number;
  voltaje_device?: number;
  timestamp?: string;
}

// Crear el contexto
const MessageFeedContext = createContext<{
  getFeed: (contextId: string) => Message[];
  addMessage: (message: Message) => void;
  prependMessages: (contextId: string, messages: Message[]) => void;
  loadOlderMessages: (contextId: string, fechaHasta: string) => Promise<void>;
  resetFeed: (contextId: string) => void;
  trimToRange: (contextId: string, startISO: string, endISO: string) => void;
}>({
  getFeed: () => [],
  addMessage: () => {},
  prependMessages: () => {},
  loadOlderMessages: async () => {},
  resetFeed: () => {},
  trimToRange: () => {},
});

// Proveedor del contexto
export const MessageFeedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [feedsByContext, setFeedsByContext] = useState<Record<string, Message[]>>({});

  // Límite de mensajes por contexto para acotar memoria.
  // Configurable vía REACT_APP_MONITOR_MAX_FEED (por defecto 5000)
  const MAX_MESSAGES = parseInt(
    process.env.REACT_APP_MONITOR_MAX_FEED || "5000",
    10
  );

  const normalizeMessage = (message: Message) => {
    const raw = message as any;
    const timestamp = message.timestamp || raw.recvTime || raw.timestamp;
    if (!timestamp) {
      return message;
    }
    return { ...message, timestamp };
  };

  // Función para agregar mensajes nuevos al historial
  const addMessage = (newMessage: Message) => {
    if (!newMessage.contextName) return;
    const normalized = normalizeMessage(newMessage);
    setFeedsByContext((prev) => {
      const feed = prev[newMessage.contextName!] || [];
      return {
        ...prev,
        [newMessage.contextName!]: [...feed, normalized].slice(-MAX_MESSAGES),
      };
    });
  };

  // Función para agregar mensajes antiguos al inicio del historial
  const prependMessages = (contextId: string, messages: Message[]) => {
    setFeedsByContext((prev) => {
      const feed = prev[contextId] || [];
      return {
        ...prev,
        // Mantenemos solo los más recientes N (descartamos los más viejos)
        [contextId]: [...messages, ...feed].slice(-MAX_MESSAGES),
      };
    });
  };

  // Función para limpiar el historial de un contexto específico
  const resetFeed = (contextId: string) => {
    setFeedsByContext((prev) => ({ ...prev, [contextId]: [] }));
  };

  // Recortar feed al rango visible actual (para achicar ventana al paneo derecha)
  const trimToRange = (contextId: string, startISO: string, endISO: string) => {
    const start = new Date(startISO).getTime();
    const end = new Date(endISO).getTime();
    if (isNaN(start) || isNaN(end)) return;
    setFeedsByContext((prev) => {
      const feed = prev[contextId] || [];
      let latest: Message | null = null;
      let latestTs = -Infinity;
      for (const m of feed) {
        const raw = m as any;
        const t = raw?.timestamp || raw?.recvTime;
        const ts = t ? new Date(t).getTime() : NaN;
        if (!isNaN(ts) && ts >= latestTs) {
          latestTs = ts;
          latest = m;
        }
      }

      const trimmed = feed.filter((m) => {
        const raw = m as any;
        const t = raw?.timestamp || raw?.recvTime;
        const ts = t ? new Date(t).getTime() : NaN;
        return !isNaN(ts) && ts >= start && ts <= end;
      });

      if (latest && !trimmed.includes(latest)) {
        trimmed.push(latest);
      }

      trimmed.sort((a, b) => {
        const ta = new Date(((a as any).timestamp || (a as any).recvTime) ?? 0).getTime();
        const tb = new Date(((b as any).timestamp || (b as any).recvTime) ?? 0).getTime();
        return ta - tb;
      });

      return { ...prev, [contextId]: trimmed };
    });
  };

  // Fetch older data for a specific context and prepend it to the feed
  const loadOlderMessages = async (contextId: string, fechaHasta: string) => {
    // Ventana ampliada para cargar más datos cuando se hace scroll rápido (por defecto 2 horas)
    const intervalMs = parseInt(
      process.env.REACT_APP_MONITOR_INTERVAL || "7200000",
      10
    );
    const fechaHastaIso = new Date(fechaHasta).toISOString();
    const fechaDesde = new Date(new Date(fechaHastaIso).getTime() - intervalMs).toISOString();

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST ?? ""}/api/v1/contexts/${contextId}`,
        { params: { fecha_desde: fechaDesde, fecha_hasta: fechaHastaIso } }
      );

      // El endpoint devuelve un objeto { data: [...], reduced: boolean }
      // por lo que debemos extraer el arreglo de la propiedad "data".
      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data.data;

      const olderMsgs: Message[] = (responseData || []).map((m: any) => ({
        ...m,
        // normalizar nombres de atributos para coincidir con los mensajes en tiemp real
        voltaje_device: m.voltaje_device ?? m.voltajeDevice,
        temperatura_device: m.temperatura_device ?? m.temperaturaDevice,
        timestamp: m.recvTime,
        contextName: contextId,
      }));

      olderMsgs.sort((a, b) => {
        const ta = new Date(a.timestamp!).getTime();
        const tb = new Date(b.timestamp!).getTime();
        return ta - tb;
      });

      if (olderMsgs.length) {
        prependMessages(contextId, olderMsgs);
      }
    } catch (err) {
      console.error("Error fetching older data", err);
    }
  };

  return (
    <MessageFeedContext.Provider
      value={{
        getFeed: (ctx: string) => feedsByContext[ctx] || [],
        addMessage,
        prependMessages,
        loadOlderMessages,
        resetFeed,
        trimToRange,
      }}
    >
      {children}
    </MessageFeedContext.Provider>
  );
};

// Hook para usar el contexto
export const useMessageFeed = () => useContext(MessageFeedContext);
